"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Link from "next/link";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { showToast } from "@/components/ui/sonner";
import { PasswordStrength } from "../password-strength";
import { SocialAuthButtons } from "../social-buttons";
import { AuthPageHeader } from "../page-header";
import { SubmitButton } from "@/components/ui/button";
import { Input, PasswordInput } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { useRegister } from "../../hooks/use-register";
import { passwordFieldSchema } from "../../lib/password-policy";
import { Icon } from "@/components/ui/icon";
import { appendInvitationToken } from "@/lib/invitation-links";
import { NEUROSCALE_LINKS } from "@/lib/neuroscale";

const registerSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: passwordFieldSchema,
  terms: z.literal(true),
});

type RegisterValues = z.infer<typeof registerSchema>;

interface SignUpProps {
  invitationToken?: string | null;
  invitedEmail?: string | null;
}

export function SignUp({
  invitationToken = null,
  invitedEmail = null,
}: SignUpProps) {
  const register = useRegister();
  const loginHref = appendInvitationToken("/login", invitationToken || null);
  const isInvited = Boolean(invitationToken);
  const lockedEmail = isInvited ? invitedEmail ?? "" : "";

  const form = useForm<RegisterValues>({
    resolver: zodResolver(registerSchema),
    mode: "all",
    defaultValues: { email: lockedEmail, password: "", terms: true },
  });

  function handleSubmit(values: RegisterValues) {
    // For the invited path the email is pre-filled and locked client-side;
    // we still send what the form holds because the backend re-verifies
    // it against the invitation token (defense in depth).
    register.mutate({
      email: isInvited && invitedEmail ? invitedEmail : values.email,
      password: values.password,
      ...(invitationToken ? { invitation_token: invitationToken } : {}),
    });
  }

  function onInvalid() {
    const errors = form.formState.errors;
    if (errors.password) {
      showToast.error("Password requirements", {
        description: errors.password.message || "Your password doesn't meet the security requirements.",
      });
    } else if (errors.email) {
      showToast.error("Invalid email", {
        description: errors.email.message || "Please enter a valid email address.",
      });
    }
  }

  return (
    <>
      <AuthPageHeader
        title="Welcome to Arbi"
        subtitle="Create an account to start using Arbi."
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit, onInvalid)} className="flex flex-col gap-6">
          <div className="flex flex-wrap gap-x-3 gap-y-6">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel required>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      variant="auth"
                      size="default"
                      placeholder="mail@neuroscale.ai"
                      autoComplete="email"
                      autoFocus={!isInvited}
                      className="rounded-sm"
                      readOnly={isInvited}
                      aria-readonly={isInvited}
                      {...field}
                    />
                  </FormControl>
                  {isInvited ? (
                    <p className="text-xs text-contrast/60 mt-1">
                      Email locked to your invitation.
                    </p>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel required>Password</FormLabel>
                  <FormControl>
                    <PasswordInput
                      placeholder="Enter password"
                      autoComplete="new-password"
                      autoFocus={isInvited}
                      className="rounded-sm"
                      {...field}
                    />
                  </FormControl>
                  <PasswordStrength password={field.value} />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="terms"
            render={({ field }) => (
              <Label className="items-start gap-2.5 cursor-pointer">
                <span className="mt-0.5 shrink-0">
                  <Checkbox checked={field.value} onChange={field.onChange} />
                </span>
                <span className="text-xs text-contrast/40 leading-relaxed">
                  By proceeding you acknowledge that you have read, understood and agree to our{" "}
                  <Link href={NEUROSCALE_LINKS.terms} className="underline text-contrast/60 hover:text-contrast/80" tabIndex={-1}>
                    Terms and Conditions
                  </Link>{" "}
                  and{" "}
                  <Link href={NEUROSCALE_LINKS.privacy} className="underline text-contrast/60 hover:text-contrast/80" tabIndex={-1}>
                    Privacy Policy
                  </Link>.
                </span>
              </Label>
            )}
          />

          <div className="flex flex-col gap-3 items-center">
            <SubmitButton type="submit" variant="linear-ai" size="auth" loading={register.isPending} disabled={register.isPending || !form.formState.isValid}>
              Get started with Arbi <Icon name="IconArrowRight" size={16} />
            </SubmitButton>

            <div className="w-full h-px bg-contrast/10" />
            <SocialAuthButtons variant="signup" />
            <div className="w-full h-px bg-contrast/10" />

            <p className="text-sm text-contrast/70 leading-5">
              Already have an account?{" "}
              <Link href={loginHref} className="font-medium text-heading hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </Form>
    </>
  );
}
